import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { Route as RouteIcon, Clock } from "lucide-react";

const makeIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${color};width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
  });

const originIcon = makeIcon("#2563EB");
const destIcon = makeIcon("#10B981");
const liveIcon = L.divIcon({
  className: "",
  html: `<div style="width:16px;height:16px;border-radius:50%;background:#ef4444;border:3px solid white;box-shadow:0 0 0 6px rgba(239,68,68,.25)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 11);
    else if (points.length >= 2) map.fitBounds(points, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export function MapView({ origin, destination, originLabel = "Origin", destinationLabel = "Destination", height = 280, showRoute = true, live = null }) {
  const [routeCoords, setRouteCoords] = useState(null);
  const [info, setInfo] = useState(null);

  const hasBoth = origin?.lat != null && destination?.lat != null;

  useEffect(() => {
    if (!showRoute || !hasBoth) { setRouteCoords(null); setInfo(null); return; }
    let active = true;
    (async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const data = await res.json();
        if (!active) return;
        if (data.routes && data.routes[0]) {
          setRouteCoords(data.routes[0].geometry.coordinates.map((c) => [c[1], c[0]]));
          setInfo({ distance: data.routes[0].distance, duration: data.routes[0].duration });
        } else { setRouteCoords(null); setInfo(null); }
      } catch {
        if (active) { setRouteCoords(null); setInfo(null); }
      }
    })();
    return () => { active = false; };
  }, [showRoute, hasBoth, origin?.lat, origin?.lng, destination?.lat, destination?.lng]);

  const points = [];
  if (origin?.lat != null) points.push([origin.lat, origin.lng]);
  if (destination?.lat != null) points.push([destination.lat, destination.lng]);
  const center = points[0] || [22.3511, 78.6677];
  const livePt = live?.lat != null ? [[live.lat, live.lng]] : [];
  const boundsPoints = (routeCoords && routeCoords.length ? [...routeCoords] : [...points]).concat(livePt);

  const km = info ? (info.distance / 1000).toFixed(0) : null;
  const mins = info ? Math.round(info.duration / 60) : null;
  const durLabel = mins != null ? (mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`) : null;

  return (
    <div className="relative" data-testid="map-view">
      {info && (
        <div className="absolute z-[400] top-3 left-3 flex items-center gap-3 rounded-xl bg-white/90 backdrop-blur border border-slate-200 px-3 py-1.5 shadow-sm text-xs font-semibold text-slate-700" data-testid="map-route-info">
          <span className="flex items-center gap-1.5 text-primary"><RouteIcon className="w-3.5 h-3.5" /> {km} km</span>
          <span className="flex items-center gap-1.5 text-slate-500"><Clock className="w-3.5 h-3.5" /> ~{durLabel} drive</span>
        </div>
      )}
      <div style={{ height }} className="overflow-hidden rounded-2xl border border-slate-200">
        <MapContainer center={center} zoom={points.length ? 6 : 5} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {origin?.lat != null && <Marker position={[origin.lat, origin.lng]} icon={originIcon}><Popup>{originLabel}</Popup></Marker>}
          {destination?.lat != null && <Marker position={[destination.lat, destination.lng]} icon={destIcon}><Popup>{destinationLabel}</Popup></Marker>}
          {live?.lat != null && <Marker position={[live.lat, live.lng]} icon={liveIcon}><Popup>Traveller (live)</Popup></Marker>}
          {routeCoords ? (
            <Polyline positions={routeCoords} pathOptions={{ color: "#2563EB", weight: 4 }} />
          ) : (
            hasBoth && <Polyline positions={points} pathOptions={{ color: "#2563EB", weight: 3, dashArray: "6 8" }} />
          )}
          <FitBounds points={boundsPoints} />
        </MapContainer>
      </div>
    </div>
  );
}
